/**
 * HeroSection — full-featured hero renderer for the dynamic page builder.
 * Reads the rich HeroConfig from section_config and renders all visual elements.
 * Backward-compatible: legacy configs (title / subtitle / cta_text) still work.
 */

import { pageHref } from '../../../utils/pageHref';
import React, { useState, useEffect } from 'react';

// ─── Defaults (mirror HeroEditor.DEFAULT_HERO_CONFIG in arcee-admin) ─────────

const DEFAULT = {
  layoutType: 'centered',
  sectionHeight: 'auto',
  sectionHeightPx: 600,
  contentMaxWidth: 'medium',
  paddingTop: 80,
  paddingBottom: 80,
  textAlignment: 'center',
  bgType: 'gradient',
  bgColor: '#1e293b',
  bgGradientFrom: '#6366f1',
  bgGradientTo: '#0ea5e9',
  bgGradientType: 'linear',
  bgGradientAngle: 135,
  bgImageUrl: '',
  bgImagePosition: 'center',
  bgImageSize: 'cover',
  bgOverlayColor: '#000000',
  bgOverlayOpacity: 40,
  bgParallax: false,
  badgeVisible: false,
  badgeText: '',
  badgeColor: '#7c3aed',
  badgeStyle: 'pill',
  badgeLink: '',
  headlineText: 'Welcome',
  headlineFontSize: 'xl',
  headlineColor: '#ffffff',
  headlineHighlightWord: '',
  headlineHighlightStyle: 'color',
  headlineHighlightColor: '#f59e0b',
  headlineTypewriter: false,
  headlineTypewriterWords: [],
  headlineFontFamily: '',
  subheadlineVisible: true,
  subheadlineText: '',
  subheadlineFontSize: 'md',
  subheadlineColor: '#cbd5e1',
  bodyFontFamily: '',
  primaryCTA: { label: 'Get Started', linkType: 'url', url: '/sign-in', targetSectionId: '', targetPageId: '', targetPageSlug: '', icon: '', iconPosition: 'left', openNewTab: false, style: 'filled', bgColor: '', textColor: '' },
  secondaryCTA: { visible: false, label: 'Learn More', linkType: 'url', url: '#', targetSectionId: '', targetPageId: '', targetPageSlug: '', icon: '', style: 'outlined', openNewTab: false, isVideoTrigger: false, videoUrl: '', bgColor: '', textColor: '' },
  buttonLayout: 'side-by-side',
  socialProofVisible: false,
  ratingVisible: false,
  ratingValue: 5,
  ratingCount: '',
  userCountText: '',
  avatarsVisible: false,
  avatarImages: [],
  visualType: 'none',
  visualUrl: '',
  visualDeviceFrame: 'none',
  visualPosition: 'right',
  visualWidthPercent: 50,
  entranceAnimation: 'none',
  scrollIndicatorVisible: false,
  shapeDivider: 'none',
  shapeDividerColor: '#ffffff',
};

const HEADLINE_SIZES = {
  sm: 'clamp(1.5rem, 2.5vw, 2rem)',
  md: 'clamp(2rem, 3.5vw, 2.75rem)',
  lg: 'clamp(2.5rem, 4.5vw, 3.5rem)',
  xl: 'clamp(3rem, 5.5vw, 4.5rem)',
};

const SUB_SIZES = { sm: '0.95rem', md: '1.1rem', lg: '1.25rem' };

const MAX_WIDTHS = {
  narrow: '640px',
  medium: '900px',
  wide:   '1200px',
  full:   '100%',
};

// ─── Headline with highlight ──────────────────────────────────────────────────

function HeadlineText({ cfg }) {
  const text      = cfg.headlineText || '';
  const word      = cfg.headlineHighlightWord || '';
  const hStyle    = cfg.headlineHighlightStyle || 'color';
  const hColor    = cfg.headlineHighlightColor || '#f59e0b';
  const fontSize  = typeof cfg.headlineFontSize === 'number'
    ? cfg.headlineFontSize + 'px'
    : (HEADLINE_SIZES[cfg.headlineFontSize] || HEADLINE_SIZES.xl);
  const baseStyle = {
    fontSize,
    fontWeight: 800,
    color: cfg.headlineColor || '#ffffff',
    lineHeight: 1.15,
    fontFamily: cfg.headlineFontFamily || 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  };

  if (!word || !text.includes(word)) {
    return <h1 style={baseStyle}>{text}</h1>;
  }

  const parts = text.split(word);
  const hi = hStyle === 'underline'
    ? <span style={{ textDecoration: 'underline', textDecorationColor: hColor, textUnderlineOffset: '4px' }}>{word}</span>
    : hStyle === 'mark'
      ? <mark style={{ background: hColor, color: '#fff', borderRadius: '4px', padding: '0 4px' }}>{word}</mark>
      : <span style={{ color: hColor }}>{word}</span>;

  return (
    <h1 style={baseStyle}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>{part}{i < parts.length - 1 && hi}</React.Fragment>
      ))}
    </h1>
  );
}

// ─── Typewriter effect ────────────────────────────────────────────────────────

function TypewriterWord({ words, color }) {
  const [idx, setIdx]       = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!words || words.length === 0) return;
    const fade = setTimeout(() => setVisible(false), 2600);
    const next = setTimeout(() => {
      setIdx(i => (i + 1) % words.length);
      setVisible(true);
    }, 3000);
    return () => { clearTimeout(fade); clearTimeout(next); };
  }, [idx, words]);

  if (!words || words.length === 0) return null;
  return (
    <span style={{
      color,
      display: 'inline-block',
      transition: 'opacity 0.3s',
      opacity: visible ? 1 : 0,
      borderBottom: `2px solid ${color}`,
      paddingBottom: 1,
    }}>
      {words[idx]}
    </span>
  );
}

// ─── CTA Button ───────────────────────────────────────────────────────────────

function CTAButton({ cta, theme, isPrimary, onClick }) {
  if (!cta) return null;
  const s = cta.style || (isPrimary ? 'filled' : 'outlined');
  const primary = theme?.primary || 'var(--primary-color)';

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4em',
    padding: '0.75rem 1.75rem',
    fontWeight: 700,
    fontSize: '1rem',
    borderRadius: 'var(--button-radius, 8px)',
    cursor: 'pointer',
    textDecoration: 'none',
    border: '2px solid transparent',
    transition: 'opacity 0.15s, transform 0.1s',
    userSelect: 'none',
  };

  const customBg   = cta.bgColor   || '';
  const customText = cta.textColor || '';

  const variant =
    s === 'filled'   ? { background: customBg || primary, color: customText || '#fff', borderColor: customBg || primary }
    : s === 'outlined' ? { background: customBg || 'transparent', color: customText || '#fff', borderColor: customText || 'rgba(255,255,255,0.7)' }
    : s === 'ghost'    ? { background: customBg || 'rgba(255,255,255,0.12)', color: customText || '#fff', borderColor: 'transparent' }
    :                    { background: customBg || 'transparent', color: customText || '#fff', borderColor: 'transparent', textDecoration: 'underline', padding: '0.75rem 0.5rem' };

  const icon = cta.icon ? <i className={`bi ${cta.icon}`} /> : null;
  const iconLeft  = icon && cta.iconPosition !== 'right' ? icon : null;
  const iconRight = icon && cta.iconPosition === 'right' ? icon : null;

  if (onClick) {
    return (
      <button type="button" onClick={onClick} style={{ ...base, ...variant }}>
        {iconLeft}{cta.label}{iconRight}
      </button>
    );
  }

  const linkType = cta.linkType || 'url';

  if (linkType === 'section' && cta.targetSectionId) {
    const handleScroll = (e) => {
      e.preventDefault();
      const el = document.querySelector(`[data-section-id="${cta.targetSectionId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    return (
      <a href={`#section-${cta.targetSectionId}`} onClick={handleScroll} style={{ ...base, ...variant }}>
        {iconLeft}{cta.label}{iconRight}
      </a>
    );
  }

  if (linkType === 'page' && (cta.targetPageSlug || cta.targetPageId)) {
    const href = cta.targetPageSlug ? pageHref(cta.targetPageSlug) : `/page/${cta.targetPageId}`;
    return (
      <a href={href} style={{ ...base, ...variant }}>
        {iconLeft}{cta.label}{iconRight}
      </a>
    );
  }

  return (
    <a
      href={cta.url || '#'}
      target={cta.openNewTab ? '_blank' : undefined}
      rel={cta.openNewTab ? 'noopener noreferrer' : undefined}
      style={{ ...base, ...variant }}
    >
      {iconLeft}{cta.label}{iconRight}
    </a>
  );
}

// ─── Video modal ──────────────────────────────────────────────────────────────

function VideoModal({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const embedUrl = url
    ? url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
    : '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, position: 'relative' }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: -40, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}
        >
          ✕
        </button>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src={embedUrl}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
            allow="autoplay; fullscreen"
            title="Video"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Device frame ─────────────────────────────────────────────────────────────

function DeviceFrame({ frameType, url, visualType }) {
  const media = visualType === 'video'
    ? <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} autoPlay muted loop playsInline />
    : url
      ? <img src={url} alt="Hero visual" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      : <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`bi ${visualType === 'video' ? 'bi-play-circle' : 'bi-image'}`} style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
        </div>;

  if (frameType === 'browser') {
    return (
      <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ background: '#1e293b', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
          <div style={{ flex: 1, background: '#0f172a', borderRadius: 4, height: 16, marginLeft: 8 }} />
        </div>
        <div>{media}</div>
      </div>
    );
  }

  if (frameType === 'phone') {
    return (
      <div style={{
        maxWidth: 280, margin: '0 auto',
        borderRadius: 36, border: '8px solid rgba(255,255,255,0.2)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        overflow: 'hidden',
        background: '#000',
      }}>
        <div style={{ background: '#1e293b', height: 20, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: 60, height: 6, background: '#0f172a', borderRadius: 3 }} />
        </div>
        {media}
      </div>
    );
  }

  if (frameType === 'laptop') {
    return (
      <div>
        <div style={{ borderRadius: '12px 12px 0 0', overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.5)', border: '8px solid #1e293b', borderBottom: 'none' }}>
          {media}
        </div>
        <div style={{ height: 12, background: '#1e293b', borderRadius: '0 0 4px 4px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }} />
        <div style={{ height: 6, background: '#0f172a', borderRadius: '0 0 8px 8px', width: '80%', margin: '0 auto' }} />
      </div>
    );
  }

  // no frame
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.35)' }}>
      {media}
    </div>
  );
}

// ─── Social proof ─────────────────────────────────────────────────────────────

function StarRating({ value }) {
  return (
    <span style={{ color: '#fbbf24', fontSize: '1.1rem', letterSpacing: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={i < Math.round(value) ? 'bi bi-star-fill' : 'bi bi-star'} />
      ))}
    </span>
  );
}

function AvatarStack({ images }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {images.slice(0, 5).map((src, i) => (
        <div key={i} style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.6)',
          marginLeft: i > 0 ? -10 : 0,
          background: src ? `url(${src}) center/cover` : '#7c3aed',
          flexShrink: 0,
          overflow: 'hidden',
        }} />
      ))}
    </div>
  );
}

// ─── Scroll indicator ─────────────────────────────────────────────────────────

function ScrollIndicator({ color }) {
  return (
    <>
      <style>{`
        @keyframes arcee-bounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50%       { transform: translateY(8px); opacity: 1; }
        }
      `}</style>
      <div style={{
        position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        color: color || 'rgba(255,255,255,0.7)',
        animation: 'arcee-bounce 1.5s ease-in-out infinite',
        fontSize: '1.5rem',
        zIndex: 2,
      }}>
        <i className="bi bi-chevron-double-down" />
      </div>
    </>
  );
}

// ─── Shape divider ────────────────────────────────────────────────────────────

function ShapeDivider({ shape, color }) {
  if (!shape || shape === 'none') return null;
  const fill = color || '#ffffff';
  const paths = {
    wave:     'M0,30 C360,70 1080,0 1440,30 L1440,60 L0,60 Z',
    diagonal: 'M0,0 L1440,60 L1440,60 L0,60 Z',
    curve:    'M0,60 Q720,0 1440,60 L1440,60 L0,60 Z',
  };
  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0, zIndex: 2 }}>
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
        <path d={paths[shape]} fill={fill} />
      </svg>
    </div>
  );
}

// ─── Entrance animation styles ────────────────────────────────────────────────

const ANIM_STYLES = `
  @keyframes arcee-fade-in  { from { opacity: 0; }                          to { opacity: 1; } }
  @keyframes arcee-slide-up { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes arcee-zoom-in  { from { opacity: 0; transform: scale(0.93); }  to { opacity: 1; transform: scale(1); } }
`;

const ANIM_MAP = {
  'fade-in':  'arcee-fade-in  0.7s ease forwards',
  'slide-up': 'arcee-slide-up 0.7s ease forwards',
  'zoom-in':  'arcee-zoom-in  0.6s ease forwards',
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function HeroSection({ config, theme, appBranding }) {
  const [videoOpen, setVideoOpen] = useState(false);

  // ── Backward-compatibility: detect legacy config format ──
  const isLegacy = config && !('bgType' in config);
  const cfg = isLegacy
    ? {
        ...DEFAULT,
        headlineText:       config.title    || DEFAULT.headlineText,
        subheadlineVisible: Boolean(config.subtitle),
        subheadlineText:    config.subtitle || '',
        bgType:             config.image_url ? 'image' : 'gradient',
        bgImageUrl:         config.image_url || '',
        bgOverlayOpacity:   Math.round((config.overlay_opacity ?? 0.5) * 100),
        primaryCTA: { ...DEFAULT.primaryCTA, label: config.cta_text || 'Get Started', url: config.cta_link || '/sign-in' },
      }
    : { ...DEFAULT, ...config };

  // Merge nested CTA objects so partial saves still work
  const primaryCTA  = { ...DEFAULT.primaryCTA,  ...(cfg.primaryCTA  || {}) };
  const secondaryCTA = { ...DEFAULT.secondaryCTA, ...(cfg.secondaryCTA || {}) };

  const themeGradient = theme?.gradientBackground || 'var(--gradient-bg)';
  const themePrimary  = theme?.primary            || 'var(--primary-color)';

  // ── Background style ──
  const sectionBg = {};
  if (cfg.bgType === 'gradient') {
    if (cfg.bgGradientFrom && cfg.bgGradientTo) {
      const c1 = cfg.bgGradientFrom;
      const c2 = cfg.bgGradientTo;
      sectionBg.background = cfg.bgGradientType === 'radial'
        ? `radial-gradient(circle, ${c1}, ${c2})`
        : `linear-gradient(${cfg.bgGradientAngle ?? 135}deg, ${c1}, ${c2})`;
    } else {
      sectionBg.background = themeGradient;
    }
  } else if (cfg.bgType === 'color') {
    sectionBg.background = cfg.bgColor || '#1e293b';
  } else if (cfg.bgType === 'image' && cfg.bgImageUrl) {
    sectionBg.backgroundImage    = `url(${cfg.bgImageUrl})`;
    sectionBg.backgroundSize     = cfg.bgImageSize     || 'cover';
    sectionBg.backgroundPosition = cfg.bgImagePosition || 'center';
    sectionBg.backgroundAttachment = cfg.bgParallax ? 'fixed' : 'scroll';
  } else if (cfg.bgType === 'image') {
    sectionBg.background = '#0f172a';
  } else {
    sectionBg.background = themeGradient;
  }

  const needsOverlay = cfg.bgType === 'image';

  // ── Layout ──
  const isSplit      = cfg.layoutType === 'split-left' || cfg.layoutType === 'split-right';
  const visualRight  = cfg.layoutType === 'split-left' || cfg.visualPosition === 'right';
  const visualLeft   = cfg.layoutType === 'split-right';
  const isCenter     = !isSplit && cfg.textAlignment === 'center';
  const textAlign    = isCenter ? 'center' : 'left';
  const contentAlign = isCenter ? 'center' : 'flex-start';

  const sectionMinHeight = cfg.sectionHeight === 'full-viewport' ? '100vh'
    : cfg.sectionHeight === 'custom' ? `${cfg.sectionHeightPx || 600}px`
    : undefined;

  const contentMaxWidth = MAX_WIDTHS[cfg.contentMaxWidth] || MAX_WIDTHS.medium;

  const visualWidthPct = `${cfg.visualWidthPercent || 50}%`;
  const contentWidthPct = `${100 - (cfg.visualWidthPercent || 50)}%`;

  // ── Animation ──
  const animation = ANIM_MAP[cfg.entranceAnimation] || undefined;

  // ── Font loading ──
  const fontsToLoad = [cfg.headlineFontFamily, cfg.bodyFontFamily].filter(Boolean);
  const fontLink = fontsToLoad.length > 0
    ? `https://fonts.googleapis.com/css2?${fontsToLoad.map(f => `family=${f.replace(/ /g, '+')}:wght@400;600;700;800`).join('&')}&display=swap`
    : null;

  const showVisual = cfg.visualType && cfg.visualType !== 'none';

  return (
    <>
      {/* Inject animation + font styles */}
      <style>{ANIM_STYLES}</style>
      {fontLink && <link rel="stylesheet" href={fontLink} />}

      {/* Section root */}
      <div
        style={{
          ...sectionBg,
          position: 'relative',
          overflow: 'hidden',
          minHeight: sectionMinHeight,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {/* Background video */}
        {cfg.bgType === 'video' && cfg.bgVideoUrl && (
          <video
            src={cfg.bgVideoUrl}
            autoPlay muted loop playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
          />
        )}

        {/* Overlay */}
        {needsOverlay && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            background: cfg.bgOverlayColor || '#000000',
            opacity: (cfg.bgOverlayOpacity ?? 40) / 100,
          }} />
        )}

        {/* Content wrapper */}
        <div
          style={{
            position: 'relative', zIndex: 2,
            width: '100%', maxWidth: contentMaxWidth,
            margin: '0 auto',
            paddingTop: cfg.paddingTop ?? 80,
            paddingBottom: cfg.paddingBottom ?? 80,
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            animation,
          }}
        >
          {/* Split layout: visual on left (split-right type) */}
          {isSplit && visualLeft && showVisual && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
              <div style={{ width: visualWidthPct, flexShrink: 0 }}>
                <DeviceFrame frameType={cfg.visualDeviceFrame} url={cfg.visualUrl} visualType={cfg.visualType} />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <HeroContent cfg={cfg} primaryCTA={primaryCTA} secondaryCTA={secondaryCTA} theme={theme} themePrimary={themePrimary} isCenter={false} onVideoClick={() => setVideoOpen(true)} />
              </div>
            </div>
          )}

          {/* Split layout: visual on right (split-left type) or not split */}
          {!(isSplit && visualLeft) && (
            <div style={{
              display: isSplit ? 'flex' : 'block',
              alignItems: 'center',
              gap: isSplit ? '3rem' : undefined,
            }}>
              <div style={{ flex: isSplit ? 1 : undefined, textAlign: isSplit ? 'left' : textAlign }}>
                <HeroContent cfg={cfg} primaryCTA={primaryCTA} secondaryCTA={secondaryCTA} theme={theme} themePrimary={themePrimary} isCenter={isCenter} contentAlign={contentAlign} onVideoClick={() => setVideoOpen(true)} />
              </div>

              {/* Visual on right (split-left) */}
              {isSplit && visualRight && showVisual && (
                <div style={{ width: visualWidthPct, flexShrink: 0 }}>
                  <DeviceFrame frameType={cfg.visualDeviceFrame} url={cfg.visualUrl} visualType={cfg.visualType} />
                </div>
              )}
            </div>
          )}

          {/* Visual below content */}
          {!isSplit && showVisual && cfg.visualPosition === 'below' && (
            <div style={{ marginTop: '3rem', maxWidth: `${cfg.visualWidthPercent || 80}%`, margin: '3rem auto 0' }}>
              <DeviceFrame frameType={cfg.visualDeviceFrame} url={cfg.visualUrl} visualType={cfg.visualType} />
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        {cfg.scrollIndicatorVisible && <ScrollIndicator color={cfg.headlineColor} />}

        {/* Shape divider */}
        <ShapeDivider shape={cfg.shapeDivider} color={cfg.shapeDividerColor} />
      </div>

      {/* Video modal */}
      {videoOpen && secondaryCTA.isVideoTrigger && secondaryCTA.videoUrl && (
        <VideoModal url={secondaryCTA.videoUrl} onClose={() => setVideoOpen(false)} />
      )}
    </>
  );
}

// ─── Hero content block (shared between split-left and split-right) ────────────

function HeroContent({ cfg, primaryCTA, secondaryCTA, theme, themePrimary, isCenter, contentAlign, onVideoClick }) {
  const textAlign    = cfg.textAlignment || (isCenter ? 'center' : 'left');
  const alignItems   = contentAlign || (isCenter ? 'center' : 'flex-start');
  const showTypewriter = cfg.headlineTypewriter && cfg.headlineTypewriterWords?.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems, textAlign }}>

      {/* Badge */}
      {cfg.badgeVisible && cfg.badgeText && (
        <div style={{ marginBottom: '1rem' }}>
          {cfg.badgeLink
            ? <a href={cfg.badgeLink} style={{ textDecoration: 'none' }}><BadgeEl cfg={cfg} /></a>
            : <BadgeEl cfg={cfg} />
          }
        </div>
      )}

      {/* Headline */}
      <div style={{ marginBottom: '1rem' }}>
        <HeadlineText cfg={cfg} />
        {showTypewriter && (
          <div style={{
            fontSize: typeof cfg.headlineFontSize === 'number'
              ? cfg.headlineFontSize + 'px'
              : (HEADLINE_SIZES[cfg.headlineFontSize] || HEADLINE_SIZES.xl),
            fontWeight: 800, lineHeight: 1.15, marginTop: '0.25rem',
          }}>
            <TypewriterWord words={cfg.headlineTypewriterWords} color={cfg.headlineHighlightColor || '#f59e0b'} />
          </div>
        )}
      </div>

      {/* Subheadline */}
      {cfg.subheadlineVisible && cfg.subheadlineText && (
        <p style={{
          fontSize: SUB_SIZES[cfg.subheadlineFontSize] || SUB_SIZES.md,
          color: cfg.subheadlineColor || '#cbd5e1',
          lineHeight: 1.65,
          maxWidth: '65ch',
          margin: '0 0 1.75rem',
          fontFamily: cfg.bodyFontFamily || 'inherit',
        }}>
          {cfg.subheadlineText}
        </p>
      )}

      {/* CTA buttons */}
      {(primaryCTA.label || (secondaryCTA.visible && secondaryCTA.label)) && (
        <div style={{
          display: 'flex',
          flexDirection: cfg.buttonLayout === 'stacked' ? 'column' : 'row',
          flexWrap: cfg.buttonLayout === 'full-width' ? 'nowrap' : 'wrap',
          gap: '0.75rem',
          alignItems,
          width: cfg.buttonLayout === 'full-width' ? '100%' : undefined,
          marginBottom: cfg.socialProofVisible ? '1.75rem' : 0,
        }}>
          {primaryCTA.label && (
            <div style={{ flex: cfg.buttonLayout === 'full-width' ? 1 : undefined }}>
              <CTAButton cta={primaryCTA} theme={theme} isPrimary />
            </div>
          )}
          {secondaryCTA.visible && secondaryCTA.label && (
            <div style={{ flex: cfg.buttonLayout === 'full-width' ? 1 : undefined }}>
              {secondaryCTA.isVideoTrigger
                ? <CTAButton cta={secondaryCTA} theme={theme} onClick={onVideoClick} />
                : <CTAButton cta={secondaryCTA} theme={theme} />
              }
            </div>
          )}
        </div>
      )}

      {/* Social proof */}
      {cfg.socialProofVisible && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems }}>
          {/* Avatars + rating row */}
          {(cfg.avatarsVisible || cfg.ratingVisible || cfg.userCountText) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: isCenter ? 'center' : 'flex-start' }}>
              {cfg.avatarsVisible && cfg.avatarImages?.length > 0 && (
                <AvatarStack images={cfg.avatarImages} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                {cfg.ratingVisible && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <StarRating value={cfg.ratingValue ?? 5} />
                    {cfg.ratingCount && (
                      <span style={{ fontSize: '0.85rem', color: cfg.subheadlineColor || '#cbd5e1', fontWeight: 600 }}>
                        {cfg.ratingCount}
                      </span>
                    )}
                  </div>
                )}
                {cfg.userCountText && (
                  <span style={{ fontSize: '0.85rem', color: cfg.subheadlineColor || '#cbd5e1', opacity: 0.8 }}>
                    {cfg.userCountText}
                  </span>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ─── Badge element ────────────────────────────────────────────────────────────

function BadgeEl({ cfg }) {
  const isPill     = cfg.badgeStyle === 'pill';
  const isOutlined = cfg.badgeStyle === 'outlined';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: isPill ? '0.3rem 0.9rem' : '0.25rem 0.6rem',
      borderRadius: isPill ? 100 : 5,
      background: isOutlined ? 'transparent' : cfg.badgeColor || '#7c3aed',
      border: `1.5px solid ${cfg.badgeColor || '#7c3aed'}`,
      color: isOutlined ? (cfg.badgeColor || '#7c3aed') : '#fff',
      fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.02em',
    }}>
      {cfg.badgeText}
    </span>
  );
}
